import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { CheckCircle, XCircle, Loader2, RefreshCw, Wifi } from "lucide-react";
import type { VerifyConnectionResult } from "../types";

interface ConnectionStatusProps {
  hasCertificate: boolean;
  environment: "certification" | "production";
  onVerify: () => Promise<VerifyConnectionResult>;
  isVerifying: boolean;
  lastResult: VerifyConnectionResult | null;
}

/**
 * Component to verify and display SII connection status.
 */
export function ConnectionStatus({
  hasCertificate,
  environment,
  onVerify,
  isVerifying,
  lastResult,
}: ConnectionStatusProps) {
  const environmentLabel =
    environment === "production" ? "Producción" : "Certificación";
  const environmentServer =
    environment === "production" ? "palena.sii.cl" : "maullin.sii.cl";

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <Wifi className="h-5 w-5" />
          Conexión SII
        </CardTitle>
        <CardDescription>
          Verificar conexión con el servidor {environmentServer} (
          {environmentLabel})
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-4">
        {/* Connection Result */}
        {lastResult && (
          <div
            className={`p-4 rounded-lg ${
              lastResult.success
                ? "bg-green-50 border border-green-200"
                : "bg-red-50 border border-red-200"
            }`}
          >
            <div className="flex items-start gap-3">
              {lastResult.success ? (
                <CheckCircle className="h-5 w-5 text-green-500 mt-0.5" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500 mt-0.5" />
              )}
              <div>
                <p
                  className={`font-medium ${
                    lastResult.success ? "text-green-700" : "text-red-700"
                  }`}
                >
                  {lastResult.success
                    ? "Conexión exitosa"
                    : "Error de conexión"}
                </p>
                <p
                  className={`text-sm ${
                    lastResult.success ? "text-green-600" : "text-red-600"
                  }`}
                >
                  {lastResult.message}
                </p>
              </div>
            </div>
          </div>
        )}

        {/* Environment Info */}
        <div className="flex items-center justify-between p-3 bg-muted rounded-lg">
          <div>
            <p className="text-sm font-medium">Ambiente</p>
            <p className="text-sm text-muted-foreground">{environmentLabel}</p>
          </div>
          <div className="text-right">
            <p className="text-sm font-medium">Servidor</p>
            <p className="text-sm text-muted-foreground">{environmentServer}</p>
          </div>
        </div>

        {/* Verify Button */}
        <div className="flex justify-end">
          <Button
            onClick={onVerify}
            disabled={!hasCertificate || isVerifying}
            variant={lastResult?.success ? "outline" : "default"}
          >
            {isVerifying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verificando...
              </>
            ) : (
              <>
                <RefreshCw className="mr-2 h-4 w-4" />
                {lastResult ? "Verificar de nuevo" : "Verificar conexión"}
              </>
            )}
          </Button>
        </div>

        {!hasCertificate && (
          <p className="text-sm text-muted-foreground text-center">
            Debe cargar un certificado antes de verificar la conexión
          </p>
        )}
      </CardContent>
    </Card>
  );
}
